import { useState } from "react";
import { useTranslation } from "react-i18next";
import wmUrl from "../../../assets/brand/n-white.svg";
import { Icon } from "../../../shared/ui";
import { loc } from "../../hero/mock";
import { HOME, PENDING, RECENT, SPARK, type Lang } from "./data";
import {
  Avatar,
  Money,
  PaymentRow,
  PendingStack,
  QuickActions,
  SectionLabel,
  Sparkline,
  TabBar,
  TrendChip,
  type QuickAction,
  type StackCard,
  type TabItem,
} from "./kit";
import { ScreenHost } from "./ScreenHost";
import { useScript } from "./useScript";
import "./home.css";

/** Handoff HowHome: «Tasdiqlash» is clicked at 1700 ms; the card flies out over 360 ms (ds PendingStack `out`). */
const T_ACCEPT = 1700;
const T_FLY = 360;

// Step 1 — Asosiy (handoff how-screens.html HowHome = ds/kit-home.jsx KitHome + TabBar). Green hero,
// quick actions, the pending stack and recent payments. Script: the front pending card is accepted —
// QABUL stamp, fly-out, toast. Rest state (prerender, reduced motion) = 4 cards, no toast.
export function HomeScreen({ play, still }: { play: boolean; still: boolean }) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";
  const [index, setIndex] = useState(0);
  const [flying, setFlying] = useState(false);

  const toast = useScript(play && !still, ({ later, toast: showToast }) => {
    later(() => {
      // ds Card button → onAction (toast) + out (stamp + fly), then the next card moves up.
      setFlying(true);
      showToast(t("hero.mock.toast_paid"));
      later(() => {
        setFlying(false);
        setIndex((n) => n + 1);
      }, T_FLY);
    }, T_ACCEPT);
  });

  const quick: QuickAction[] = [
    { icon: "i-docPlus", label: t("how.app.home.qa_new") },
    { icon: "i-debtIn", label: t("how.app.home.qa_incoming"), badge: 3 },
    { icon: "i-userPlus", label: t("how.app.home.qa_client") },
    { icon: "i-chart", label: t("how.app.home.qa_stats") },
  ];
  const tabs: TabItem[] = [
    { icon: "i-home", label: t("hero.mock.tab_home") },
    { icon: "i-handshake", label: t("hero.mock.tab_agreements") },
    { icon: "i-inbox", label: t("hero.mock.tab_inbox"), badge: true },
    { icon: "i-wallet", label: t("hero.mock.tab_debts") },
  ];
  const cards: StackCard[] = PENDING.map((p) => ({
    name: p.name,
    product: loc(p.product, lang),
    when: p.when === "yesterday" ? t("hero.stack.yesterday") : loc(p.when, lang),
    amount: p.amount,
    kind: p.kind,
    status:
      p.kind === "in"
        ? t("hero.stack.payment_in")
        : t(p.kind === "over" ? "hero.stack.days_late" : "hero.stack.days_until", { n: p.days }),
  }));

  return (
    <ScreenHost dark toast={still ? null : toast}>
      <div className="hk-scroll">
        <div className="hk-home-hero">
          <img className="hk-home-wm" src={wmUrl} alt="" />
          <div className="hk-home-top">
            <Avatar initials={HOME.initials} />
            <div className="hk-home-who">
              <div className="hk-home-hi">{t("how.app.home.greeting")}</div>
              <div className="hk-home-shop">{HOME.shop}</div>
            </div>
            <span className="hk-home-bell">
              <Icon name="i-bell" />
              <i />
            </span>
          </div>
          <div className="hk-home-col">{t("how.app.home.collected")}</div>
          <div className="hk-home-sum">
            <div className="hk-home-sum-l">
              <Money value={HOME.amount} size="hero" className="hk-home-amt" />
              <TrendChip label={t("how.app.home.trend")} />
            </div>
            <Sparkline data={SPARK} />
          </div>
        </div>
        <div className="hk-home-qa">
          <QuickActions items={quick} />
        </div>
        <div className="hk-home-ps">
          <PendingStack
            title={t("how.app.home.pending")}
            allLabel={t("how.app.home.all")}
            cards={cards}
            index={still ? 0 : index}
            flying={!still && flying}
            stamp={t("hero.stack.stamp_accept")}
            labels={{ accept: t("hero.stack.accept"), reject: t("hero.stack.reject"), remind: t("hero.stack.remind") }}
          />
        </div>
        <div className="hk-home-recent">
          <SectionLabel title={t("how.app.home.recent")} />
          {RECENT.map((r) => (
            <PaymentRow
              key={r.name}
              title={r.name}
              sub={`${t("how.app.home.confirmed")} · ${r.time} · ${loc(r.method, lang)}`}
              amount={r.amount}
            />
          ))}
        </div>
      </div>
      <TabBar items={tabs} on={0} />
    </ScreenHost>
  );
}
